import _camelCase from 'lodash/camelCase';
import _omit from 'lodash/omit';
import _cloneDeep from 'lodash/cloneDeep';
import _difference from 'lodash/difference';

import { getSalesItemName } from '../utils';

import actionTypes from '../actions/actionTypes';

export const INITIAL_STATE = {
  salesData: {},
  performanceData: {},
  loading: false,
  error: false,
  todaysSalesData: {},
  loadingTodaysSales: false,
  todaysSalesError: false,
  associates: null,
  departments: null,
  designers: null,
  myTeam: null,
  loadingManagerSales: false,
  loadingManagerSalesTree: false,
  managerSalesError: false,
  managerStoreId: '',
  managerSalesTreeCache: {},
  loadingManagerAssociateSalesMetrics: false,
  managerAssociateSalesMetrics: {
    count: null,
    items: [],
  },
  stylistManagerSalesData: {
    count: null,
    items: [],
    totalAssociateSales: null,
  },
  loadingStylistManagerSales: false,
  stylistManagerSalesError: false,
  stylistManagerSalesSaSearchTerm: null,
  isBackFromAssociatePerfHomePage: {
    isBack: null,
    cachedFilterType: null,
    cachedCalendarRange: {},
  },
};

const updateManagersSalesData = ({ dataType, count, items }, state) => {
  const storeKey = _camelCase(dataType);
  const currentItems = state[storeKey]?.items || [];

  return {
    loadingManagerSales: false,
    [storeKey]: {
      count,
      items: [...currentItems, ...items],
    },
  };
};

const mapTreeItems = ({ items, parentIds = [], noExpand, divisionsMasterList = {} }) =>
  items
    .filter(({ id }) => !!getSalesItemName(id, divisionsMasterList))
    .map((item) => {
      const { id } = item;

      return {
        children: noExpand ? [] : [{ id: 'loading', loading: true }],
        name: getSalesItemName(id, divisionsMasterList),
        parentIds,
        ...item,
      };
    });

const updateManagersSalesTreeItems = ({ nodeIds, items, parentIds = [] }, rootLevelItems, divisionsMasterList = {}) => {
  const actualNodeIds = nodeIds.reduce((acc, currentNodeId, currentIndex) => {
    const isNodePresent = !!rootLevelItems.find(({ id }) => id === currentNodeId);
    if (isNodePresent) {
      return nodeIds.slice(currentIndex);
    }

    return acc;
  }, []);

  if (!actualNodeIds.length) {
    const noExpand = parentIds.length === 2;
    return mapTreeItems({ items, parentIds, noExpand, divisionsMasterList });
  }

  return rootLevelItems.reduce((acc, currentItem) => {
    if (currentItem.id === actualNodeIds[0]) {
      const updatedItem = {
        ...currentItem,
        children: updateManagersSalesTreeItems(
          {
            items,
            nodeIds: actualNodeIds.slice(1),
            /**
             * order of parent nodes is crucial left most item in the array
             * is a root node the right most is a last child
             * e.g. [divisionId, groupId]
             */
            parentIds: [...parentIds, actualNodeIds[0]],
          },
          currentItem.children,
          divisionsMasterList,
        ),
      };
      acc.push(updatedItem);
    } else {
      acc.push(currentItem);
    }

    return acc;
  }, []);
};

const updateManagersSalesTree = ({ items, count, divisionId, groupId, divisionsMasterList = {} }, state) => {
  const rootLevelItems = state.departments?.items || [];
  const nodeIds = [divisionId, groupId].filter((nodeId) => !!nodeId);
  const actualNodeIds = nodeIds.reduce((acc, currentNodeId, currentIndex) => {
    const isNodePresent = !!rootLevelItems.find(({ id }) => id === currentNodeId);
    if (isNodePresent) {
      return nodeIds.slice(currentIndex);
    }

    return acc;
  }, []);

  const parentIds = _difference(nodeIds, actualNodeIds);

  const filteredItems = updateManagersSalesTreeItems(
    { nodeIds, items, parentIds },
    rootLevelItems,
    divisionsMasterList,
  );

  const filteredCount = count - filteredItems?.length;
  const updatedCount = count - filteredCount;

  return {
    loadingManagerSales: false,
    loadingManagerSalesTree: false,
    departments: {
      count: !nodeIds.length ? updatedCount : state.departments?.count,
      items: filteredItems,
    },
  };
};

const performanceReducer = (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case actionTypes.PERF_DATA_LOADING:
      return {
        ...state,
        loading: true,
        error: false,
      };
    case actionTypes.SALES_DATA_SUCCESS:
      return {
        ...state,
        salesData: payload,
        loading: false,
      };
    case actionTypes.PERFORMANCE_DATA_SUCCESS:
      return {
        ...state,
        performanceData: payload,
        loading: false,
      };
    case actionTypes.PERFORMANCE_DATA_RESET:
      return {
        ...state,
        performanceData: {
          grossSales: 0,
          returns: 0,
          netSales: 0,
        },
      };
    case actionTypes.PERF_DATA_ERROR:
      return {
        ...state,
        loading: false,
        error: true,
      };
    case actionTypes.TODAYS_SALES_SUCCESS:
      return {
        ...state,
        todaysSalesData: payload.data,
        managerStoreId: payload.storeId,
        loadingTodaysSales: false,
      };
    case actionTypes.TODAYS_SALES_ERROR:
      return {
        ...state,
        todaysSalesError: true,
        loadingTodaysSales: false,
      };
    case actionTypes.TODAYS_SALES_LOADING:
      return {
        ...state,
        todaysSalesError: false,
        loadingTodaysSales: true,
      };
    case actionTypes.MANAGER_SALES_SUCCESS:
      return {
        ...state,
        ...updateManagersSalesData(payload, state),
      };
    case actionTypes.MANAGER_SALES_TREE_LOADING:
      return {
        ...state,
        loadingManagerSalesTree: true,
      };
    case actionTypes.MANAGER_SALES_TREE_ERROR:
      return {
        ...state,
        loadingManagerSalesTree: false,
      };
    case actionTypes.MANAGER_SALES_TREE_SUCCESS:
      return {
        ...state,
        ...updateManagersSalesTree(payload, state),
      };
    case actionTypes.MANAGER_SALES_TREE_SAVE_STATE:
      return {
        ...state,
        managerSalesTreeCache: {
          ...state.managerSalesTreeCache,
          [payload.pageId]: {
            processedNodeIds: payload.processedNodeIds,
            expandedNodesMap: payload.expandedNodesMap,
            tree: _cloneDeep(state.departments),
            dateFilter: payload.dateFilter,
          },
        },
      };
    case actionTypes.MANAGER_SALES_TREE_RESTORE:
      return {
        ...state,
        departments: state.managerSalesTreeCache[payload.pageId]?.tree || null,
        managerSalesTreeCache: _omit(state.managerSalesTreeCache, payload.pageId),
      };
    case actionTypes.MANAGER_SALES_TREE_CLEAR_CACHE:
      return {
        ...state,
        managerSalesTreeCache: {},
      };
    case actionTypes.MANAGER_SALES_TREE_CLEAR:
      return {
        ...state,
        departments: null,
      };
    case actionTypes.MANAGER_SALES_CLEAR:
      return {
        ...state,
        associates: null,
        departments: null,
        designers: null,
        myTeam: null,
      };
    case actionTypes.MANAGER_SALES_ERROR:
      return {
        ...state,
        managerSalesData: [],
        managerSalesError: true,
        loadingManagerSales: false,
      };
    case actionTypes.MANAGER_SALES_LOADING:
      return {
        ...state,
        managerSalesError: false,
        loadingManagerSales: true,
      };
    case actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_LOADING:
      return {
        ...state,
        loadingManagerAssociateSalesMetrics: true,
      };
    case actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_SUCCESS:
      return {
        ...state,
        loadingManagerAssociateSalesMetrics: false,
        managerAssociateSalesMetrics: {
          count: payload.count,
          items: [...state.managerAssociateSalesMetrics.items, ...payload.items],
        },
      };
    case actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_ERROR:
      return {
        ...state,
        loadingManagerAssociateSalesMetrics: false,
      };
    case actionTypes.MANAGER_ASSOCIATE_SALES_METRICS_CLEAR:
      return {
        ...state,
        managerAssociateSalesMetrics: {
          ...INITIAL_STATE.managerAssociateSalesMetrics,
        },
      };
    case actionTypes.STYLIST_MANAGER_SALES_LOADING:
      return {
        ...state,
        loadingStylistManagerSales: true,
      };
    case actionTypes.STYLIST_MANAGER_SALES_SUCCESS:
      return {
        ...state,
        loadingStylistManagerSales: false,
        stylistManagerSalesData: {
          count: payload.count,
          items: [...state.stylistManagerSalesData.items, ...payload.items],
          totalAssociateSales: payload.totalAssociateSales,
        },
      };
    case actionTypes.STYLIST_MANAGER_SALES_ERROR:
      return {
        ...state,
        stylistManagerSalesError: true,
      };
    case actionTypes.CLEAR_STYLIST_MANAGER_SALES:
      return {
        ...state,
        stylistManagerSalesData: { ...INITIAL_STATE.stylistManagerSalesData },
      };
    case actionTypes.STYLIST_MANAGER_SALES_SA_SEARCH_TERM:
      return {
        ...state,
        stylistManagerSalesSaSearchTerm: payload,
      };
    case actionTypes.STYLIST_MANAGER_SALES_SA_SEARCH_TERM_CLEAR:
      return {
        ...state,
        stylistManagerSalesSaSearchTerm: INITIAL_STATE.stylistManagerSalesSaSearchTerm,
      };
    case actionTypes.STYLIST_MANAGER_SALES_SET_IS_BACK_FROM_ASSOCIATE_PERF_HOME_PAGE:
      return {
        ...state,
        isBackFromAssociatePerfHomePage: payload,
      };
    case actionTypes.STYLIST_MANAGER_SALES_SET_IS_BACK_FROM_ASSOCIATE_PERF_HOME_PAGE_CLEAR:
      return {
        ...state,
        isBackFromAssociatePerfHomePage: INITIAL_STATE.isBackFromAssociatePerfHomePage,
      };
    case actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_LOADING:
      return {
        ...state,
        loadingTodaysSales: true,
      };
    case actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_SUCCESS:
      return {
        ...state,
        loadingTodaysSales: false,
        todaysSalesError: false,
        todaysSalesData: payload.data,
      };
    case actionTypes.TODAYS_SALES_FOR_STYLIST_MANAGER_ERROR:
      return {
        ...state,
        todaysSalesError: true,
        loadingTodaysSales: false,
      };
    default:
      return state;
  }
};

export default performanceReducer;
